/**
 * UI Constants
 * Constants for UI-related values (timing, dimensions, breakpoints)
 */

// ===== BRANDING =====

/** Separator between the brand name and the page name in the document title ("GeoMarkup - Dashboard") */
export const DOCUMENT_TITLE_SEPARATOR = ' - ';

// ===== BREAKPOINTS =====

/**
 * Breakpoints for responsive layout detection
 *
 * Layout Mode Decision Tree:
 * - Width < MOBILE_MAX_WIDTH (768px) → MOBILE (always, phones)
 * - Width <= TABLET_PORTRAIT_MAX_WIDTH (1100px) → MOBILE (any window ≤ 1100px)
 * - Width >= DESKTOP_MIN_WIDTH (1101px) → DESKTOP
 *
 * CSS breakpoints kept in sync via PostCSS variables in postcss.config.js:
 *   $(mobile-breakpoint) = 1100px  (max-width for mobile)
 *   $(desktop-breakpoint) = 1101px (min-width for desktop)
 */

/** Maximum width for mobile layout - phones always show mobile (pixels) */
export const MOBILE_MAX_WIDTH = 767;

/** Maximum width for mobile layout - any window ≤ 1100px shows mobile (pixels) */
export const TABLET_PORTRAIT_MAX_WIDTH = 1100;

/** Minimum width for desktop layout (pixels) */
export const DESKTOP_MIN_WIDTH = 1101;

/** Minimum window width to show the fullscreen right panel (map + comments) (pixels) */
export const RIGHT_PANEL_MIN_WIDTH = 1400;

// ===== TIMING CONSTANTS =====

/** Delay before showing toolbar guide on first use (milliseconds) */
export const TOOLBAR_GUIDE_SHOW_DELAY = 500;

/** Delay before loading map component for better initial performance (milliseconds) */
export const MAP_DEFERRED_LOAD_DELAY = 500;

// ===== TOOLBAR GUIDE PANEL DIMENSIONS =====

/** Width of the toolbar guide panel (pixels) */
export const TOOLBAR_GUIDE_PANEL_WIDTH = 500;

/** Fixed height of the toolbar guide content area for carousel (pixels) */
export const TOOLBAR_GUIDE_CONTENT_HEIGHT = 640;

/** Horizontal padding for carousel slides (no arrow space needed) (pixels) */
export const TOOLBAR_GUIDE_SLIDE_PADDING_HORIZONTAL = 16;

/** Vertical padding at bottom of slides to make space for larger indicators (pixels) */
export const TOOLBAR_GUIDE_SLIDE_PADDING_BOTTOM = 30;

/** Total number of slides in toolbar guide carousel */
export const TOOLBAR_GUIDE_TOTAL_SLIDES = 5;

// ===== LAYOUT SPACING =====

/** Standard gap spacing for UI elements (pixels) */
export const UI_GAP_STANDARD = 20;

/** Small animation offset for slide-in effects (pixels) */
export const UI_ANIMATION_OFFSET_SMALL = 20;

// ===== TYPOGRAPHY =====

/** Standard letter spacing for uppercase text */
export const LETTER_SPACING_STANDARD = '0.5px';

/** Medium font weight for semi-bold text */
export const FONT_WEIGHT_MEDIUM = 500;

/** Semi-bold font weight for headings and emphasis text */
export const FONT_WEIGHT_SEMIBOLD = 600;

// ===== INPUT LABEL STYLING =====

/** Font size for input labels (pixels) */
export const INPUT_LABEL_FONT_SIZE = 14;

/** Font weight for input labels (semi-bold) */
export const INPUT_LABEL_FONT_WEIGHT = 600;

/** Gap between input label and input field (pixels) */
export const INPUT_LABEL_MARGIN_BOTTOM = 4;

/** Min rows a description-style autosize textarea starts at (app-wide) */
export const TEXTAREA_MIN_ROWS = 3;

/** Max rows an autosize textarea expands to before it scrolls internally (app-wide) */
export const TEXTAREA_MAX_ROWS = 5;

// ===== BORDERS =====

/** Standard thin border width (pixels) */
export const BORDER_WIDTH_THIN = 1;

// ===== SCROLLBARS =====

/** Overlay scrollbar thumb size (width/height) (pixels) — matches the 8px bar themed in global.css */
export const SCROLLBAR_SIZE = 8;

/** Default Mantine ScrollArea reveal behavior — bar appears on hover and always overlays (never reserves layout space) */
export const SCROLLBAR_OVERLAY_TYPE = 'hover' as const;

// ===== GUIDE CAROUSEL DIMENSIONS =====

/** Height of guide carousel on desktop (pixels) */
export const GUIDE_CAROUSEL_HEIGHT = 110;

/** Width of guide carousel on desktop (pixels) */
export const GUIDE_CAROUSEL_WIDTH_DESKTOP = 328;

/** Close button size in guide carousel (pixels) */
export const GUIDE_CAROUSEL_CLOSE_BUTTON_SIZE = 24;

/** Close button icon size in guide carousel (pixels) */
export const GUIDE_CAROUSEL_CLOSE_ICON_SIZE = 18;

/** Horizontal padding for carousel slides (no arrow space needed) (pixels) */
export const GUIDE_CAROUSEL_SLIDE_PADDING_HORIZONTAL = 16;

/** Bottom padding for carousel slides (space for larger indicators) (pixels) */
export const GUIDE_CAROUSEL_SLIDE_PADDING_BOTTOM = 30;

// ===== SHARED CAROUSEL INDICATOR CONSTANTS =====

/** Size of inactive carousel indicator dots (pixels) - shared across all carousels */
export const CAROUSEL_INDICATOR_SIZE = 12;

/** Width of active carousel indicator - elongated pill shape (pixels) */
export const CAROUSEL_INDICATOR_ACTIVE_WIDTH = 32;

/** Height of active carousel indicator (pixels) */
export const CAROUSEL_INDICATOR_ACTIVE_HEIGHT = 12;

/** Gap between carousel indicator dots (pixels) */
export const CAROUSEL_INDICATORS_GAP = 8;

/** Bottom position offset for carousel indicators (pixels) */
export const CAROUSEL_INDICATORS_BOTTOM = 16;

// ===== MAP VIEW PRECISION =====

/** Coordinate comparison precision threshold for detecting pan vs zoom */
export const COORD_PRECISION_THRESHOLD = 0.5;

/** Zoom comparison precision threshold for detecting zoom changes */
export const ZOOM_PRECISION_THRESHOLD = 0.1;

// ===== PANEL DIMENSIONS =====

/** Form panel right margin (pixels) */
export const TASK_FORM_PANEL_MARGIN = 16;

/** Default width for all SlidePanel instances (pixels) */
export const PANEL_DEFAULT_WIDTH = 480;

/** Minimum resizable width for desktop SlidePanel (pixels). Kept in sync with
 *  the `min-width` floor in SlidePanel.module.css (.containerDesktop). */
export const SLIDE_PANEL_MIN_WIDTH = 420;

/** LocalStorage key for unified panel width */
export const PANEL_WIDTH_KEY = 'panelWidth';

/** LocalStorage key — mobile "swipe down to minimize" hint already seen */
export const SWIPE_DOWN_HINT_SEEN_KEY = 'swipeDownHintSeen';

/** LocalStorage key — mobile "swipe up to restore" hint already seen */
export const SWIPE_UP_HINT_SEEN_KEY = 'swipeUpHintSeen';

// ===== TASKS PANEL (SIDEBAR) DIMENSIONS =====

/** TasksPanel (sidebar) width on desktop (pixels) */
export const TASKS_PANEL_WIDTH_DESKTOP = 380;

/**
 * Navigation bar width (pixels) — JS mirror for layout math (camera offsets,
 * map padding). Must stay in sync with the CSS token `--app-sidebar-width`
 * in `global.css`, which drives the actual rail width.
 */
export const NAVIGATION_BAR_WIDTH = 76;

// ===== LINE DRAWING HELPERS =====

/** Point marker size on desktop (pixels) */
export const LINE_POINT_MARKER_SIZE_DESKTOP = 24;

/** Point marker size on mobile (pixels) */
export const LINE_POINT_MARKER_SIZE_MOBILE = 32;

/** Point marker border width (pixels) */
export const LINE_POINT_MARKER_BORDER_WIDTH = 2;

/** Instructions overlay bottom position on desktop (pixels) */
export const LINE_INSTRUCTIONS_BOTTOM_DESKTOP = 100;

/** Instructions overlay z-index (below form 1000, above map controls) */
export const LINE_INSTRUCTIONS_Z_INDEX = 900;

// ===== MOBILE MINIMIZED PANEL =====

/** Mobile minimized panel bar height (pixels) - iOS standard touch target */
export const MOBILE_MINIMIZED_BAR_HEIGHT = 64;

/** Mobile minimized panel bar height WITH collapsed actions (Cancel/Save buttons) (pixels) */
export const MOBILE_MINIMIZED_BAR_WITH_ACTIONS_HEIGHT = 84;

/** Spacing above mobile minimized bar for tooltip positioning (pixels) */
export const MOBILE_MINIMIZED_BAR_SPACING = 20;

/** Mobile drawing instructions top position - below search bar (pixels) */
export const MOBILE_INSTRUCTIONS_TOP = 148; // 84px (search bar top) + 48px (search bar height) + 16px (gap)

/** Mobile form top margin to show map context (pixels) - 2x Mantine spacing-md */
export const MOBILE_FORM_MAP_GAP = 32;

/** Minimum touch target height for mobile drag indicator (pixels) - iOS HIG recommends 44pt */
export const MOBILE_DRAG_TOUCH_TARGET_HEIGHT = 48;

/** Mobile toolbar spacing from form bar (pixels) - matches CSS --toolbar-mobile-spacing */
export const MOBILE_TOOLBAR_SPACING = 16;

/**
 * Total height of bottom UI stack during mobile drawing mode (pixels)
 * Used for auto-pan boundary calculation to avoid triggering pan over UI elements
 * Breakdown: form bar (84px) + spacing (16px) + toolbar (56px) = 156px
 */
export const MOBILE_DRAWING_BOTTOM_UI_HEIGHT = 156;

// ===== ANIMATION DURATIONS =====

/** SlidePanel animation duration for enter/exit transitions (milliseconds) */
export const SLIDE_PANEL_ANIMATION_DURATION = 500;

/** Swipe hint auto-hide timeout duration (milliseconds) */
export const SWIPE_HINT_AUTO_HIDE_DURATION = 8000;

/** Offset for fade-out animation before swipe hint auto-hide (milliseconds) */
export const SWIPE_HINT_FADE_OUT_OFFSET = 500;

// ===== BULK ACTIONS BAR =====

/** Bulk actions bar animation duration (milliseconds) - matches CSS 0.3s transition */
export const BULK_ACTIONS_ANIMATION_DURATION = 300;

/** Bulk actions bar content max-width (pixels) - prevents bar from becoming too wide on large panels */
export const BULK_ACTIONS_MAX_WIDTH = 600;

// ===== TOOLBOX EXPANDABLE PANELS =====

/** Icon size for toolbar buttons (pixels) */
export const TOOLBOX_ICON_SIZE = 20;

/** Narrow panel width (single column of tool buttons) (pixels) */
export const TOOLBOX_PANEL_WIDTH_NARROW = 56;

/** Standard panel width (full controls and settings) (pixels) */
export const TOOLBOX_PANEL_WIDTH_STANDARD = 240;

/** Panel animation duration (milliseconds) */
export const TOOLBOX_PANEL_ANIMATION_DURATION = 200;

/** Minimal gap between expandable panels (pixels) - tight spacing for Canva-style layout */
export const TOOLBOX_PANEL_GAP = 2;

/** ToolButton height (pixels) - Mantine ActionIcon size 'lg' */
export const TOOLBOX_BUTTON_HEIGHT = 40;

/** Gap between ToolButtons (pixels) - matches var(--mantine-spacing-xs) */
export const TOOLBOX_BUTTON_GAP = 8;

/** ToolButton height + gap for Level 3 offset calculation (pixels) */
export const TOOLBOX_BUTTON_HEIGHT_WITH_GAP =
  TOOLBOX_BUTTON_HEIGHT + TOOLBOX_BUTTON_GAP; // 48px

// ===== DELETE BUTTON (SELECTION MODE) =====

/** Delete button size (width and height) (pixels) */
export const DELETE_BUTTON_SIZE = 32;

/** Delete button icon size (pixels) */
export const DELETE_BUTTON_ICON_SIZE = 20;

/** Delete button offset from selected point - horizontal (pixels) */
export const DELETE_BUTTON_POINT_OFFSET_X = 16;

/** Delete button offset from selected point - vertical (pixels) */
export const DELETE_BUTTON_POINT_OFFSET_Y = -16;

/** Delete button offset from click position - horizontal (pixels) */
export const DELETE_BUTTON_GEOMETRY_OFFSET_X = 15;

/** Delete button offset from click position - vertical (pixels) */
export const DELETE_BUTTON_GEOMETRY_OFFSET_Y = -15;

/** Delete button viewport edge padding (prevents button from going off-screen) (pixels) */
export const DELETE_BUTTON_VIEWPORT_PADDING = 10;

// ===== GLOBAL ICON SIZES =====

/** Double extra small icon size for compact metadata indicators (pixels) */
export const ICON_SIZE_2XS = 11;

/** Extra small icon size for loaders and tiny UI elements (pixels) */
export const ICON_SIZE_XS = 12;

/** Small icon size for action buttons (pixels) */
export const ICON_SIZE_SM = 16;

/** Check icon size for the basemap multi-select selection badge (pixels) */
export const CHECK_BADGE_ICON_SIZE = 14;

/** Medium (default) icon size (pixels) */
export const ICON_SIZE_MD = 18;

/** Large icon size for emphasis and checkboxes (pixels) */
export const ICON_SIZE_LG = 20;

/** Tab icon size for navigation tabs (pixels) */
export const ICON_SIZE_TAB = 14;

/** Empty state illustration icon size (pixels) */
export const ICON_SIZE_EMPTY_STATE = 48;

/** Sidebar navigation icon size (pixels) */
export const ICON_SIZE_SIDEBAR = 17;

/** Default icon stroke width for navigation and tab icons */
export const ICON_STROKE_WIDTH_DEFAULT = 1.7;

// ===== MAP CONTROLS POSITIONING =====

/** MapControlsToolbar right margin - standard panel margin (pixels) */
export const MAP_CONTROLS_RIGHT_MARGIN = 8;

/** LayerSettingsPanel width (pixels) */
export const LAYER_SETTINGS_PANEL_WIDTH = 280;

// ===== TOOLBAR MOBILE LAYOUT =====

/** Mobile toolbar divider width (pixels) */
export const MOBILE_TOOLBAR_DIVIDER_WIDTH = 2;

/** Mobile toolbar divider height as percentage of toolbar height */
export const MOBILE_TOOLBAR_DIVIDER_HEIGHT_PERCENTAGE = 60;

/** Spacing above mobile toolbar for scroll hint positioning (pixels) */
export const MOBILE_TOOLBAR_SCROLL_HINT_SPACING = 8;

// ===== COLOR PICKER =====

/** Default height for color picker panel when height cannot be measured (pixels) */
export const COLOR_PICKER_DEFAULT_HEIGHT = 400;

/** Maximum height for color picker panel (pixels) */
export const COLOR_PICKER_MAX_HEIGHT = 400;

// ===== PREVIEW PANEL (MOBILE TASK DETAILS) =====

/** Preview panel height on mobile - shows header + tabs + basic info (pixels) */
export const PREVIEW_PANEL_HEIGHT = 350;

/** Preview panel map offset percentage - panel occupies this % of screen height */
export const PREVIEW_PANEL_MAP_OFFSET_PERCENT = 50;

/**
 * Extra bottom clearance (px) reserved on mobile above the map-config bottom
 * sheet for the stacked map-area overlays (zoom pill + drag hint) + spacing /
 * safe-area, so the center pin stays above them rather than just above the
 * panel. Mirrors the `--zoom-pill-clearance` (64px) + pill height + spacing
 * used by MapConfigPreview.module.css.
 */
export const MAP_CONFIG_MOBILE_OVERLAY_CLEARANCE = 160;

// ===== STATUS TABS =====

/** Status tab padding on desktop (horizontal and vertical) */
export const STATUS_TAB_PADDING_DESKTOP = '8px 16px';

/** Status tab padding in compact variant */
export const STATUS_TAB_PADDING_COMPACT = '6px 12px';

/** Status tab padding on mobile (reduced for space efficiency) */
export const STATUS_TAB_PADDING_MOBILE = '6px 12px';

/** Status tab badge size (width and height) (pixels) */
export const STATUS_TAB_BADGE_SIZE = 22;

/** Compact status tab badge size (pixels) */
export const STATUS_TAB_BADGE_SIZE_COMPACT = 18;

// ===== ONBOARDING LOGO =====

/** Onboarding logo container size (width and height) (pixels) */
export const ONBOARDING_LOGO_CONTAINER_SIZE = 72;

/** Onboarding logo border radius (pixels) */
export const ONBOARDING_LOGO_BORDER_RADIUS = 20;

/** Onboarding logo icon size (width and height) (pixels) */
export const ONBOARDING_LOGO_ICON_SIZE = 32;

/** Onboarding logo bottom margin (pixels) */
export const ONBOARDING_LOGO_MARGIN_BOTTOM = 20;

// ===== TEAM MANAGEMENT PANEL =====

/** Max height for member/invitation list containers (pixels) */
export const TEAM_PANEL_LIST_MAX_HEIGHT = 400;

/** Desktop width of the team management drawer (pixels) */
export const TEAM_PANEL_DRAWER_WIDTH = 440;

/** Milliseconds per day for expiry calculations */
export const MS_PER_DAY = 1000 * 60 * 60 * 24;

/** Height of the drawer header area (pixels) */
export const DRAWER_HEADER_HEIGHT = 60;

/** Dot separator size for metadata lines (pixels) */
export const DOT_SEPARATOR_SIZE = 3;

/** Vertical gap between member detail lines (pixels) */
export const MEMBER_DETAILS_GAP = 2;

// ===== WORKSPACE CREATION FORM =====

/** Icon size for section headers in workspace creation form (pixels) */
export const SECTION_ICON_SIZE = 20;

/** Icon size for add button in invitation input (pixels) */
export const ADD_BUTTON_ICON_SIZE = 20;

/** Max height of the scrollable invitation list before it scrolls (pixels) */
export const INVITATION_LIST_MAX_HEIGHT = 300;

// ===== BATCH INVITATION MODAL =====

/** Header icon container size - width and height (pixels) */
export const INVITE_MODAL_HEADER_ICON_SIZE = 48;

/** Header icon inner size - Users icon (pixels) */
export const INVITE_MODAL_HEADER_ICON_INNER_SIZE = 24;

/** Header icon container border radius (pixels) */
export const INVITE_MODAL_HEADER_ICON_RADIUS = 12;

/** Close button size - width and height (pixels) */
export const INVITE_MODAL_CLOSE_BUTTON_SIZE = 36;

/** Close button X icon size (pixels) */
export const INVITE_MODAL_CLOSE_ICON_SIZE = 20;

/** Close button border radius (pixels) */
export const INVITE_MODAL_CLOSE_BUTTON_RADIUS = 10;

/** Maximum height for invitation list scroll area (pixels) */
export const INVITE_MODAL_LIST_MAX_HEIGHT = 240;

/** Modal container max width (pixels) */
export const INVITE_MODAL_MAX_WIDTH = 480;

/** Send button icon size (pixels) */
export const INVITE_MODAL_SEND_ICON_SIZE = 16;

// ===== VIEW TOGGLE =====

/** Icon size for ViewToggle buttons (pixels) */
export const VIEW_TOGGLE_ICON_SIZE = 15;

// ===== USER AVATAR =====

/** Small avatar size (pixels) */
export const USER_AVATAR_SIZE_SM = 24;

/** Medium avatar size (pixels) */
export const USER_AVATAR_SIZE_MD = 26;

/** Font size for small avatar initials (pixels) */
export const USER_AVATAR_FONT_SIZE_SM = 10;

/** Font size for medium avatar initials (pixels) */
export const USER_AVATAR_FONT_SIZE_MD = 11;

// ===== TASK PROGRESS BAR =====

/** Bar height for table variant (pixels) */
export const PROGRESS_BAR_HEIGHT_TABLE = 6;

/** Bar height for compact variant (pixels) */
export const PROGRESS_BAR_HEIGHT_COMPACT = 4;

/** Minimum bar width for table variant (pixels) */
export const PROGRESS_BAR_MIN_WIDTH_TABLE = 60;

/** Minimum bar width for compact variant (pixels) */
export const PROGRESS_BAR_MIN_WIDTH_COMPACT = 40;

/** Bar height for kanban variant (pixels) */
export const PROGRESS_BAR_HEIGHT_KANBAN = 6;

/** Gap between bar and text in table variant (pixels) */
export const PROGRESS_BAR_TEXT_GAP = 8;

/**
 * `bottom` for map-area floating overlays lifted above the mobile SlidePanel.
 * Shared by `MapControlsToolbar` (main map) and the configurator's zoom pill /
 * indicator (driven via the `--toolbar-bottom-offset` CSS custom property).
 */
export const MOBILE_OVERLAY_OFFSET_ABOVE_PREVIEW =
  'calc(var(--mantine-spacing-md) + var(--preview-panel-height, 350px) + var(--safe-area-bottom))';
export const MOBILE_OVERLAY_OFFSET_ABOVE_MINIMIZED =
  'calc(var(--mantine-spacing-md) + var(--drag-handle-height, 64px) + var(--safe-area-bottom))';
